import type { FormVariant } from "@homeinstead/analytics";

import { track } from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";
import { useEffect, useRef } from "react";

type UsePostalCodeAnalyticsParams = {
  postalCode: string;
  formVariant: FormVariant;
  locationsAvailable: boolean;
  isValid: boolean;
  isFetching: boolean;
  error?: Error;
};

const POSTAL_CODE_LOOKUP_ERROR_STATUS = "postal_code_lookup_error";
const POSTAL_CODE_VALID_STATUS = "postal_code_valid";
const POSTAL_CODE_NOT_SERVED_STATUS = "postal_code_not_served";

/**
 * Returns the status of the postal code based on the locations available and the error.
 */
function getPostalCodeStatus(locationsAvailable: boolean, isError?: boolean) {
  if (isError) {
    return POSTAL_CODE_LOOKUP_ERROR_STATUS;
  }
  if (!locationsAvailable) {
    return POSTAL_CODE_NOT_SERVED_STATUS;
  }
  return POSTAL_CODE_VALID_STATUS;
}

/**
 * Tracks the postal code validation event.
 */
export function usePostalCodeAnalytics({
  postalCode,
  formVariant,
  locationsAvailable,
  isValid,
  isFetching,
  error,
}: UsePostalCodeAnalyticsParams) {
  const trackedPostalCodes = useRef<Set<string>>(new Set());

  useEffect(() => {
    // If the postal code is empty, is fetching, or is not valid, don't track.
    if (!postalCode || isFetching || !isValid) {
      return;
    }
    // If the postal code has already been tracked, don't track again.
    if (trackedPostalCodes.current.has(postalCode)) {
      return;
    }

    // The track call is deferred. Capture location now so source middleware
    // does not stamp a later route if the timeout fires after navigation.
    const pagePath = window.location.pathname;
    const pageUrl = window.location.href;

    const timeout = setTimeout(() => {
      track(EventName.TRACKED_CLICK, {
        event_type: "postal_code_validation",
        form_variant: formVariant,
        page_path: pagePath,
        page_url: pageUrl,
        postal_code: postalCode,
        postal_code_status: getPostalCodeStatus(locationsAvailable, !!error),
      });
      trackedPostalCodes.current.add(postalCode);
      // Short delay to ensure the postal code is validated before tracking
    }, 300);
    return () => clearTimeout(timeout);
  }, [postalCode, formVariant, locationsAvailable, error, isValid, isFetching]);
}
